import { createJSONStorage, localStorageSource } from "@/storage/browser"
import { parseMachineFromPath } from "@/utils/machine-route"

// The fragment mark a machine adds to the hosted dashboard URL it opens in a
// browser on itself: its installer (backend `localaccess.OnMachineURL`).
const mark = "on_machine=1"

/**
 * Disposable cache: machines this browser was opened on. A public page that
 * probes a loopback address makes Chrome ask for local network access, so the
 * dashboard probes only these machines' loopback routes; browsers on other
 * devices are never asked. Losing it only costs the direct route until the
 * machine opens the dashboard again.
 */
const machines = createJSONStorage<string[]>("mux.machines-on-this-device.v1", {
  decode: (value) => Array.isArray(value) ? value.filter((name): name is string => typeof name === "string") : [],
}, localStorageSource)

export function machineOnThisDevice(name: string) {
  return machines.read([]).includes(name)
}

export function rememberMachineOnThisDevice(name: string) {
  const known = machines.read([])
  if (!known.includes(name)) machines.write([...known, name])
}

/** Removes the mark from the address and reports whether it was there. */
export function takeOnMachineMark() {
  const parts = location.hash.slice(1).split("&")
  const rest = parts.filter((part) => part !== mark)
  if (rest.length === parts.length) return false
  const hash = rest.filter(Boolean).join("&")
  history.replaceState(history.state, "", `${location.pathname}${location.search}${hash ? `#${hash}` : ""}`)
  return true
}

/** Records the machine of a marked dashboard link. Call before the router reads the location. */
export function consumeOnMachineLink() {
  const name = parseMachineFromPath(location.pathname)
  if (name && takeOnMachineMark()) rememberMachineOnThisDevice(name)
}
